import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { AUTO_RETRY_DELAYS_MS, aiFailure } from '../../utils/aiIdentify';
import { can } from '../../utils/permissions';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import Dialog from '../ui/Dialog';
import { LAYERS } from '../ui/layers';

/**
 * This picture beside the lab's vetted photographs, nearest first (L12;
 * docs/LIVE_UX_PLAN.md 7.2, 7.5; routes/reference_vectors.py).
 *
 * A person presses for it, since each search wakes the model. The answer is
 * the species nearest this picture, and under each its photographs, each with
 * its closeness and where it came from. It is never an identification: it is
 * labelled "closest reference", its number is a cosine and not a percentage,
 * nothing here can be accepted, and it says that a species with no references
 * can't appear at all, so a missing one proves nothing.
 *
 * A cold model (503 ai_busy) is asked again by itself, as AiIdentifyPanel does.
 * The rest of the honest failures are utils/aiIdentify.js's, with one of its
 * own: a service that gives no vectors yet.
 *
 * Whoever may vet (reference:vet) may withdraw a reference here, with a reason:
 * it leaves this answer at once, without waking the model again.
 *
 * Shown only to those holding reference:search. `s3Key` is an uploaded picture,
 * `photoId` a stored one the server looks up.
 */
export default function ReferenceCompare({ s3Key, photoId, user, requestConfig, testId = 'reference-compare' }) {
  const { t } = useTranslation('ai');
  const [phase, setPhase] = useState('idle');
  const [answer, setAnswer] = useState(null);
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [withdrawing, setWithdrawing] = useState(null);
  const timer = useRef(null);
  const asked = useRef(0);

  // Another picture: whatever was said was about the last one. Not on mounting: this effect can run after a press that
  // came straight away, and would throw that press's answer out.
  const shown = useRef(`${s3Key}|${photoId}`);
  useEffect(() => {
    if (shown.current === `${s3Key}|${photoId}`) return;
    shown.current = `${s3Key}|${photoId}`;
    asked.current += 1;
    clearTimeout(timer.current);
    setPhase('idle');
    setAnswer(null);
    setFailure(null);
  }, [s3Key, photoId]);
  useEffect(() => () => {
    asked.current += 1;
    clearTimeout(timer.current);
  }, []);

  if (!can(user, 'reference:search') || (!s3Key && !photoId)) return null;

  const ask = async (tries = 0) => {
    clearTimeout(timer.current);
    const mine = ++asked.current;
    setFailure(null);
    setPhase('running');
    setAttempt(tries);
    const body = s3Key ? { s3_key: s3Key } : { photo_id: photoId };
    try {
      const { data } = await (requestConfig
        ? api.post('/references/search', body, requestConfig)
        : api.post('/references/search', body));
      if (mine !== asked.current) return;
      setAnswer(data);
      setPhase('done');
    } catch (e) {
      if (mine !== asked.current) return;
      const found = aiFailure(e);
      if (found.busy && tries < AUTO_RETRY_DELAYS_MS.length) {
        timer.current = setTimeout(() => ask(tries + 1), AUTO_RETRY_DELAYS_MS[tries]);
        setAttempt(tries + 1);
        return;
      }
      setFailure(found);
      setPhase('failed');
    }
  };

  /** The answer without one reference, and without a species left with none. */
  const withdrawn = (vectorId) => {
    setWithdrawing(null);
    setAnswer((was) => ({
      ...was,
      compared: Math.max(0, (was.compared || 0) - 1),
      matches: was.matches
        .map((m) => ({ ...m, references: m.references.filter((r) => r.vector_id !== vectorId) }))
        .filter((m) => m.references.length),
    }));
  };

  const mayVet = can(user, 'reference:vet');
  const failureText = failure?.code === 'embeddings_not_served' ? t('references.notServed') : t(failure?.key || 'failed');

  return (
    <section style={st.box} aria-label={t('references.title')} data-testid={testId}>
      {phase === 'idle' && (
        <Button onClick={() => ask()} title={t('tip.referencesRun')} data-testid={`${testId}-run`}>
          {t('references.run')}
        </Button>
      )}
      {phase === 'running' && (
        <p role="status" style={st.muted} data-testid={`${testId}-running`}>
          {attempt > 0 ? t('warming', { attempt, total: AUTO_RETRY_DELAYS_MS.length }) : t('references.running')}
        </p>
      )}
      {phase === 'failed' && (
        <div role="alert" style={st.muted} data-testid={`${testId}-failed`}>
          <p style={{ margin: '0 0 6px' }}>{failureText}</p>
          <Button variant="ghost" onClick={() => ask()} title={t('tip.retry')} data-testid={`${testId}-retry`}>
            {t('retry')}
          </Button>
        </div>
      )}
      {phase === 'done' && (
        <>
          <h3 style={st.title}>{t('references.title')}</h3>
          {answer.matches.length === 0 ? (
            <p style={st.muted} data-testid={`${testId}-empty`}>{t('references.empty')}</p>
          ) : (
            <ol style={st.species} data-testid={`${testId}-list`}>
              {answer.matches.map((m) => (
                <li key={m.taxon_name} style={st.oneSpecies} data-testid={`${testId}-species`}>
                  <div style={st.name}>
                    {m.hebrew_name && <strong dir="auto">{m.hebrew_name}</strong>}
                    <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{m.taxon_name}</bdi>
                    <span style={st.score} title={t('tip.referencesScore')}>
                      {t('references.closeness', { score: m.score.toFixed(2) })}
                    </span>
                  </div>
                  <ul style={st.tiles}>
                    {m.references.map((r) => (
                      <Tile key={r.vector_id} r={r} t={t} mayVet={mayVet} taxon={m.taxon_name}
                        onWithdraw={() => setWithdrawing({ ...r, taxon_name: m.taxon_name })} testId={testId} />
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          )}
          <p style={st.note} data-testid={`${testId}-note`}>{t('references.note')}</p>
        </>
      )}
      {withdrawing && (
        <WithdrawDialog reference={withdrawing} requestConfig={requestConfig} t={t}
          onClose={() => setWithdrawing(null)} onDone={() => withdrawn(withdrawing.vector_id)} testId={testId} />
      )}
    </section>
  );
}

function Tile({ r, t, mayVet, taxon, onWithdraw, testId }) {
  return (
    <li style={st.tile} data-testid={`${testId}-reference`}>
      <a href={resolvePhotoUrl(r.s3_url)} target="_blank" rel="noopener noreferrer" title={t('tip.referencesOpen')}>
        <img src={resolvePhotoUrl(r.thumbnail_url || r.s3_url)} alt={t('references.alt', { taxon })} loading="lazy"
          style={st.image} />
      </a>
      <div style={st.score} title={t('tip.referencesScore')}>{t('references.closeness', { score: r.score.toFixed(2) })}</div>
      <div style={st.source} data-testid={`${testId}-source`}>
        {t(`references.source.${r.source}`, { defaultValue: r.source })}
        {r.record_code && <> · <bdi dir="ltr">{r.record_code}</bdi></>}
      </div>
      {r.vetted_by_name && <div style={st.source}>{t('references.vettedBy', { name: r.vetted_by_name })}</div>}
      {mayVet && (
        <Button variant="ghost" style={st.withdraw} onClick={onWithdraw} title={t('tip.referencesWithdraw')}
          data-testid={`${testId}-withdraw`}>
          {t('references.withdraw')}
        </Button>
      )}
    </li>
  );
}

const REASON_MIN = 3;
const REASON_MAX = 2000;

function WithdrawDialog({ reference, requestConfig, t, onClose, onDone, testId }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ready = reason.trim().length >= REASON_MIN;

  const send = async () => {
    setBusy(true);
    setError(null);
    const path = `/references/${reference.vector_id}/withdraw`;
    try {
      await (requestConfig
        ? api.post(path, { reason: reason.trim() }, requestConfig)
        : api.post(path, { reason: reason.trim() }));
      onDone();
    } catch (e) {
      // Already withdrawn by someone else: the same place to be, so it leaves the list too.
      if (e?.response?.data?.code === 'already_withdrawn') {
        onDone();
        return;
      }
      setBusy(false);
      setError(t('references.withdrawFailed'));
    }
  };

  return (
    <Dialog title={t('references.withdrawTitle', { taxon: reference.taxon_name })} onClose={onClose} busy={busy}
      dirty={!!reason.trim()} layer={LAYERS.overViewer} width={440}>
      <p style={st.dialogText}>{t('references.withdrawHint')}</p>
      <label style={st.label}>
        {t('references.withdrawReason')}
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={REASON_MAX}
          disabled={busy} style={st.field} data-testid={`${testId}-withdraw-reason`} />
      </label>
      {error && <p role="alert" style={st.error} data-testid={`${testId}-withdraw-error`}>{error}</p>}
      <ActionBar>
        <Button onClick={onClose} disabled={busy}>{t('references.cancel')}</Button>
        <Button variant="danger" onClick={send} disabled={!ready || busy} busy={busy}
          data-testid={`${testId}-withdraw-confirm`}>
          {t('references.withdraw')}
        </Button>
      </ActionBar>
    </Dialog>
  );
}

const st = {
  box: { marginTop: 10 },
  title: { margin: '0 0 6px', fontSize: 14, color: 'var(--c-text-body-fg)' },
  muted: { margin: 0, fontSize: 13, color: 'var(--c-text-muted-2)' },
  species: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 },
  oneSpecies: { minWidth: 0 },
  name: { display: 'flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'baseline', fontSize: 13, marginBottom: 4 },
  score: { fontSize: 12, color: 'var(--c-text-muted-2)', fontVariantNumeric: 'tabular-nums' },
  tiles: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 },
  tile: { width: 104, minWidth: 0, fontSize: 12 },
  image: { width: 104, height: 104, objectFit: 'cover', borderRadius: 6, display: 'block', background: 'var(--c-surface)' },
  source: { fontSize: 11, color: 'var(--c-text-muted-2)', overflowWrap: 'anywhere' },
  withdraw: { padding: '2px 6px', fontSize: 12, marginTop: 2 },
  note: { margin: '8px 0 0', fontSize: 12, color: 'var(--c-text-muted-2)' },
  dialogText: { margin: '0 0 10px', fontSize: 13, color: 'var(--c-text-body-fg)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-fg)', marginBottom: 10 },
  field: {
    display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 8, fontSize: 14,
    border: '1px solid var(--c-border-strong-2)', borderRadius: 6, background: 'var(--c-surface)', color: 'inherit',
  },
  error: { margin: '0 0 10px', fontSize: 13, color: 'var(--c-danger-3-fg)' },
};
